import axios from 'axios';
import { toast } from 'react-toastify';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getApiErrorMessages = (data) => {
  if (!Array.isArray(data?.errors)) {
    return [];
  }

  return data.errors
    .map((entry) => entry?.message)
    .filter((message) => typeof message === 'string' && message.trim().length > 0);
};

const getApiErrorMessage = (errorOrData, fallback = 'An unexpected error occurred.') => {
  const data = errorOrData?.response?.data || errorOrData;
  const validationMessages = getApiErrorMessages(data);

  if (validationMessages.length > 0) {
    return validationMessages[0];
  }

  if (typeof data?.message === 'string' && data.message.trim().length > 0) {
    return data.message;
  }

  if (typeof errorOrData?.message === 'string' && errorOrData.message.trim().length > 0) {
    return errorOrData.message;
  }

  return fallback;
};

// Token management
class TokenManager {
  static getToken() {
    return localStorage.getItem('agent_token');
  }

  static setToken(token) {
    localStorage.setItem('agent_token', token);
  }

  static removeToken() {
    localStorage.removeItem('agent_token');
    localStorage.removeItem('agent_user');
  }

  static getUser() {
    const user = localStorage.getItem('agent_user');
    return user ? JSON.parse(user) : null;
  }

  static setUser(user) {
    localStorage.setItem('agent_user', JSON.stringify(user));
  }

  static isAuthenticated() {
    return !!this.getToken();
  }
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = TokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const { response } = error;

    if (response) {
      const { status, data } = response;

      switch (status) {
        case 401:
          // Unauthorized - token expired or invalid
          TokenManager.removeToken();
          if (window.location.pathname !== '/signin') {
            toast.error('Session expired. Please sign in again.');
            window.location.href = '/signin';
          }
          break;

        case 403:
          // Forbidden - may be token/authorization issue in some backend flows
          if (
            data?.message?.toLowerCase?.().includes('unauthorized') ||
            data?.message?.toLowerCase?.().includes('token') ||
            data?.message?.toLowerCase?.().includes('jwt')
          ) {
            TokenManager.removeToken();
            if (window.location.pathname !== '/signin') {
              toast.error('Session expired. Please sign in again.');
              window.location.href = '/signin';
            }
          } else {
            toast.error(getApiErrorMessage(data, 'You do not have permission to perform this action.'));
          }
          break;

        case 400:
        case 422: {
          const validationMessages = getApiErrorMessages(data);

          if (validationMessages.length > 0) {
            validationMessages.forEach((message) => toast.error(message));
          } else {
            toast.error(getApiErrorMessage(data, 'Validation error occurred.'));
          }
          break;
        }

        case 404:
          // Not found
          toast.error(data?.message || 'Resource not found.');
          break;

        case 429:
          // Rate limit exceeded
          toast.error('Too many requests. Please try again later.');
          break;

        case 500:
          // Server error
          toast.error('Server error occurred. Please try again later.');
          break;

        default:
          // Other errors
          toast.error(getApiErrorMessage(data, 'An unexpected error occurred.'));
          break;
      }
    } else if (error.code === 'ECONNABORTED') {
      // Timeout error
      toast.error('Request timeout. Please check your connection.');
    } else if (error.message === 'Network Error') {
      // Network error
      toast.error('Network error. Please check your connection.');
    } else {
      // Other errors
      toast.error(getApiErrorMessage(error, 'An unexpected error occurred.'));
    }

    return Promise.reject(error);
  }
);

// API service class
class ApiService {
  // Generic HTTP methods
  static async get(endpoint, params = {}) {
    try {
      const response = await api.get(endpoint, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async post(endpoint, data = {}) {
    try {
      const response = await api.post(endpoint, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async put(endpoint, data = {}) {
    try {
      const response = await api.put(endpoint, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async patch(endpoint, data = {}) {
    try {
      const response = await api.patch(endpoint, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async delete(endpoint) {
    try {
      const response = await api.delete(endpoint);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // File upload method
  static async upload(endpoint, formData, onUploadProgress = null) {
    try {
      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Download method
  static async download(endpoint, filename) {
    try {
      const response = await api.get(endpoint, {
        responseType: 'blob',
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

// Export everything
export { api, ApiService, getApiErrorMessage, TokenManager };
export default ApiService;